import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import useLoadOptions from "@/hooks/useLoadOptions";
import CompanyCourse from "@/interfaces/CompanyCourse";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useBulkSendConventionsMutation } from "@/services/companies/courses/conventions";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Button as BsButton, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface Programme {
  label: string;
  url: string;
}

interface SendConventionsForm {
  courses: CompanyCourse[];
  programmes: Programme[];
}

const SendCompanyConventionsModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<{ companyId: string }>
> = ({ companyId, onHide, callback }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<SendConventionsForm>({
    defaultValues: { programmes: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "programmes",
  });

  const [bulkSendAsync] = useBulkSendConventionsMutation();

  const { loadCompanyCoursesOptions } = useLoadOptions();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<SendConventionsForm> = (data) => {
    showOrHiddenModal();
    const companyCourseIds = data.courses.map((c) => c.id);
    const programmes = data.programmes?.length ? data.programmes : undefined;

    toast.promise(
      bulkSendAsync([companyId, { companyCourseIds, programmes }]).unwrap(),
      {
        pending: "Envoi des conventions en cours...",
        success: {
          render({ data }) {
            reset();
            callback?.(data);
            const sentCount = data?.sent?.length ?? 0;
            const errorCount = data?.errors?.length ?? 0;
            if (errorCount > 0) {
              return (
                <div className="text-start">
                  <p className="mb-2">
                    {sentCount} convention(s) envoyée(s) pour signature
                  </p>
                  <p className="mb-1 fw-semi-bold">
                    {errorCount} erreur(s) :
                  </p>
                  <ul className="list-unstyled mb-0 ps-2">
                    {data!.errors.map((e) => (
                      <li key={e.courseId} className="mb-1 text-danger">
                        {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                {sentCount} convention(s) envoyée(s) pour signature
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            showOrHiddenModal();
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }
            const result = data?.data as { error?: string } | undefined;
            return (
              result?.error ||
              "Une erreur est survenue lors de l'envoi des conventions"
            );
          },
        },
      },
      { autoClose: 10000 }
    );
  };

  const onError: SubmitErrorHandler<SendConventionsForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      onHide={onHide}
      title="Envoyer des conventions pour signature"
      onSubmit={handleSubmit(onSubmit, onError)}
      requiredLabel
      beforeFooter={
        fields.length < 4 ? (
          <BsButton
            variant="primary"
            className="me-auto"
            onClick={() => append({ label: "", url: "" })}
          >
            + Ajouter un programme
          </BsButton>
        ) : undefined
      }
    >
      <Row className="gx-3">
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Formations <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              control={control}
              name="courses"
              rules={{
                required: "Veuillez sélectionner au moins une formation",
              }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={(query, options, additional) =>
                    loadCompanyCoursesOptions(query, options, {
                      ...(additional as Record<string, unknown>),
                      companyId,
                    })
                  }
                  getOptionLabel={(option) =>
                    `${option.course?.name ?? "Formation"} ${
                      option.hoursNumber ? `(${option.hoursNumber}h)` : ""
                    }`
                  }
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  onChange={field.onChange}
                  isMulti
                  placeholder="Sélectionnez une ou plusieurs formations..."
                  noOptionsMessage={() => "Aucune formation disponible"}
                />
              )}
            />
            {errors.courses && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.courses.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        {fields.length > 0 && (
          <Col lg={12}>
            <Form.Label>Programmes</Form.Label>
            {fields.map((field, index) => (
              <Row key={field.id} className="gx-2 mb-2 align-items-start">
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Intitulé"
                    isInvalid={!!errors.programmes?.[index]?.label}
                    {...register(`programmes.${index}.label`, {
                      required: "L'intitulé est requis",
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.programmes?.[index]?.label?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Control
                    type="url"
                    placeholder="https://..."
                    isInvalid={!!errors.programmes?.[index]?.url}
                    {...register(`programmes.${index}.url`, {
                      required: "Le lien est requis",
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: "L'URL doit commencer par http:// ou https://",
                      },
                    })}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.programmes?.[index]?.url?.message}
                  </Form.Control.Feedback>
                </Col>
                <Col xs="auto">
                  <BsButton
                    variant="link"
                    className="text-danger p-0 mt-1"
                    onClick={() => remove(index)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </BsButton>
                </Col>
              </Row>
            ))}
          </Col>
        )}
      </Row>

      <div className="alert alert-info mb-0">
        <small>
          <strong>Note :</strong> Les conventions seront automatiquement
          générées si nécessaire, puis envoyées pour signature à l'adresse email
          de l'entreprise.
        </small>
      </div>
    </Modal>
  );
};

export default forwardRef(SendCompanyConventionsModal);
